import {DestroyRef, Directive, inject, Input, OnInit, signal} from '@angular/core';
import {
  CodebookService,
  formatDisposalOperation,
  processRetentionTriggerCheckYearChange,
  processRetentionTriggerTypeChange,
  processRetentionTriggerYearChange
} from '|shared';
import {ClassificationSchemeDto, DisposalScheduleDto} from '|api/codebook';
import {EntityType, RetentionTriggerTypeCode} from '|api/commons';

import {combineLatest} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiFileService, FileDto, ValidRetentionTriggerDataDto} from '|api/document';
import {IczFormControl, IczFormGroup, IczOption} from '@icz/angular-form-elements';
import {FileSettleStateService} from './file-settle-state.service';
import {LoadingIndicatorService} from '@icz/angular-essentials';
import {TranslateService} from '@ngx-translate/core';
import {distinctUntilChanged} from 'rxjs/operators';
import {enumToOptions} from '@icz/angular-application-core';

@Directive()
export class AbstractFileSettleFormComponent implements OnInit {
  private codebookService = inject(CodebookService);
  protected destroyRef = inject(DestroyRef);
  protected fileSettleStateService = inject(FileSettleStateService);
  protected apiFileService = inject(ApiFileService);
  protected loadingService = inject(LoadingIndicatorService);
  protected translateService = inject(TranslateService);

  @Input({required: true}) form!: IczFormGroup;
  @Input() file!: FileDto;
  @Input() otherSettlementMethodsOptions!: IczOption[];
  isGeneralFileSettleForm = false;

  disposalSchedules: DisposalScheduleDto[] = [];
  retentionTriggerTypesOptions: IczOption[] = enumToOptions('retentionTriggerTypeCode', RetentionTriggerTypeCode);
  classificationSchemes: ClassificationSchemeDto[] = [];

  isRetentionTriggerTypeWithPeriod = signal(false);
  isExternalRetentionTriggerTypeWithPeriod = signal(false);
  allExternalRetentionTriggers: IczOption[] = [];

  get entityClassId() {
    return this.form ? this.form.get('entityClassId')!.value : null;
  }

  get documentTypeId() {
    return this.form ? this.form.get('documentTypeId')!.value : null;
  }

  get retentionTriggerTypeCodeCtrl() {
    return this.form.get('retentionTriggerTypeCode');
  }

  get retentionTriggerYearCtrl() {
    return this.form.get('yearOfRetentionPeriodStart') as IczFormControl;
  }

  get retentionTriggerCheckYearCtrl() {
    return this.form.get('triggerEventCheckYear') as IczFormControl;
  }

  get entityClassIdCtrl() {
    return this.form.get('entityClassId') as IczFormControl;
  }

  get disposalOperationCodeCtrl() {
    return this.form.get('disposalOperationCode') as IczFormControl;
  }

  get disposalYearCtrl() {
    return this.form.get('disposalYear') as IczFormControl;
  }

  codebooksLoaded() {}

  ngOnInit () {
    combineLatest([
      this.codebookService.disposalSchedules(),
      this.codebookService.externalRetentionTriggers(),
      this.codebookService.classificationSchemes(),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([disposalSchedules, externalRetentionTriggers, classificationSchemes]) => {
      this.disposalSchedules = disposalSchedules;
      this.classificationSchemes = classificationSchemes;
      this.allExternalRetentionTriggers = externalRetentionTriggers.map(rt => ({
        value: rt.id,
        label: `${rt.code} ${rt.description}`,
      }));
      this.codebooksLoaded();
    });

    this.retentionTriggerTypeCodeCtrl!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedRTT: Nullable<RetentionTriggerTypeCode>) => {
      processRetentionTriggerTypeChange(selectedRTT, this.isRetentionTriggerTypeWithPeriod, this.isExternalRetentionTriggerTypeWithPeriod, this.retentionTriggerYearCtrl, this.retentionTriggerCheckYearCtrl, true);
    });

    this.retentionTriggerYearCtrl.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      processRetentionTriggerYearChange(value, this.retentionTriggerCheckYearCtrl);
    });

    this.retentionTriggerCheckYearCtrl.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      processRetentionTriggerCheckYearChange(value, this.retentionTriggerYearCtrl);
    });
  }

  updateRetentionAndDisposalData() {
    this.loadingService.doLoading(
      this.apiFileService.fileGetValidRetentionTriggerData({
        body: [{
          entityId: this.file.id,
          entityClassId: this.entityClassId,
        }]
      }),
      this
    ).subscribe(retentionData => {
      if (retentionData.resultsPerEntity) {
        this.fillRetentionDataWithPrepare(retentionData.resultsPerEntity[0]);
        // disposal data fills non editable form controls
        this.updateDisposalData();
      }
    });
  }

  updateDisposalData() {
    this.loadingService.doLoading(this.apiFileService.fileGetDisposalData({
      body: [{
        id: this.file.id,
        entityClassId: this.entityClassId,
        entityType: EntityType.FILE,
        yearOfRetentionPeriodStart: this.retentionTriggerYearCtrl.value
      }]
    }), this
    ).subscribe(disposalData => {
      const dataForFile = disposalData[this.file.id];
      this.disposalYearCtrl.setValue(dataForFile.disposalYear);
      this.disposalOperationCodeCtrl.setValue(formatDisposalOperation(dataForFile.disposalOperationCode, this.translateService));
    });
  }

  fillRetentionSection(selectedDs: Nullable<DisposalScheduleDto>) {
    if (selectedDs) {
      this.form.get('retentionTriggerTypeCode')!.setValue(selectedDs?.retentionTriggerTypeCode);
      if (selectedDs.retentionTriggerTypeCode !== RetentionTriggerTypeCode.AFTER_SUBJECT_CREATE) {
        this.form.get('triggerEventCheckYear')!.setValue((new Date()).getFullYear() + (selectedDs.defaultTriggerEventCheckPeriod ?? 0));
      }
    }
  }

  fillRetentionDataWithPrepare(prepareData: Nullable<ValidRetentionTriggerDataDto>) {
    if (prepareData) {
      if (prepareData.defaultYearOfRetentionPeriodStart) {
        this.form.get('yearOfRetentionPeriodStart')!.setValue(prepareData.defaultYearOfRetentionPeriodStart);
      }
      this.form.get('externalRetentionTriggerIds')!.setValue(prepareData.externalRetentionTriggerIds);
    }
  }
}
